import { useResponsive } from "@design-lab/shared/hooks/useResponsive";

export type SelectionOverlayProps = {
  isSelected: boolean;
  onSelect: () => void;
  onEdit?: () => void;
  hoverAlignment?: "center" | "bottom";
  selectText?: string;
  showEditButton?: boolean;
  isRefineMode?: boolean;
};

export function SelectionOverlay({
  isSelected,
  onSelect,
  onEdit,
  hoverAlignment = "bottom",
  selectText = "Select Artwork",
  showEditButton = false,
  isRefineMode = false
}: SelectionOverlayProps) {
  const { isDesktop } = useResponsive();
  if (isSelected) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
        <svg
          viewBox="0 0 64 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="size-12 xl:size-16"
        >
          <path
            d="M31.9995 2.66699C48.1998 2.66699 61.3333 15.7998 61.3335 32C61.3335 48.2004 48.1999 61.334 31.9995 61.334C15.7993 61.3338 2.6665 48.2002 2.6665 32C2.66668 15.7999 15.7994 2.66717 31.9995 2.66699ZM45.8853 22.1143C44.8438 21.0732 43.1551 21.073 42.1138 22.1143L27.9995 36.2295L21.8853 30.1143C20.8438 29.0732 19.1551 29.073 18.1138 30.1143C17.0725 31.1555 17.0727 32.8443 18.1138 33.8857L26.1138 41.8857C27.1552 42.9271 28.8439 42.9271 29.8853 41.8857L45.8853 25.8857C46.9267 24.8443 46.9267 23.1557 45.8853 22.1143Z"
            fill="url(#paint0_linear_4197_10244)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_4197_10244"
              x1="53.574"
              y1="61.3337"
              x2="9.43591"
              y2="59.9887"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#2563eb" />
              <stop offset="1" stopColor="#a855f7" />
            </linearGradient>
          </defs>
        </svg>
        <div className="flex w-full justify-center">
          <span className="bg-cta-gradient rounded-lg px-1.5 py-0.5 text-center text-xs font-normal leading-[18px] text-white shadow-sm">
            Selected
          </span>
        </div>
      </div>
    );
  }

  const alignmentClasses =
    hoverAlignment === "center" ? "items-center justify-center" : "items-end justify-center mb-2";

  if (isDesktop) {
    if (isRefineMode) {
      return (
        <div className="absolute inset-0 flex w-full flex-col items-center justify-end gap-2 px-2 pb-2">
          <button
            data-testid="gallery-refine-image-button"
            className="bg-cta-gradient w-full cursor-pointer rounded-lg px-4 py-2 text-sm font-semibold text-white"
            onClick={e => {
              e.stopPropagation();
              onSelect();
            }}
          >
            {selectText}
          </button>
        </div>
      );
    }
    return (
      <div
        className={`absolute inset-0 flex ${alignmentClasses} rounded-lg opacity-0 transition-opacity duration-200 group-hover:opacity-100`}
        key={`hover-${isSelected}`}
      >
        <div className="flex flex-col gap-2">
          <button
            data-testid="gallery-select-image-button"
            className="bg-cta-gradient cursor-pointer rounded-lg px-4 py-2 text-sm font-semibold text-white"
            onClick={e => {
              e.stopPropagation();
              onSelect();
            }}
          >
            {selectText}
          </button>
          {showEditButton && onEdit && (
            <button
              data-testid="gallery-edit-image-button"
              className="border-cta-gradient cursor-pointer rounded-lg px-4 py-2 text-sm font-semibold text-blue-700"
              onClick={e => {
                e.stopPropagation();
                onEdit();
              }}
            >
              Edit Artwork
            </button>
          )}
        </div>
      </div>
    );
  }

  // Mobile view
  if (isRefineMode) return null;
  return (
    <div
      className={`absolute inset-0 flex ${alignmentClasses} rounded-lg opacity-100 transition-opacity duration-200`}
      key={`hover-${isSelected}`}
    >
      <div className="flex flex-row gap-2">
        <button
          data-testid="gallery-select-image-button"
          className="bg-cta-gradient cursor-pointer rounded px-2 py-1 text-xs font-semibold text-white"
          onClick={e => {
            e.stopPropagation();
            onSelect();
          }}
        >
          {selectText}
        </button>
        {showEditButton && onEdit && (
          <button
            data-testid="gallery-refine-image-button"
            className="border-cta-gradient cursor-pointer rounded px-2 py-1 text-xs font-semibold text-blue-700"
            onClick={e => {
              e.stopPropagation();
              onEdit();
            }}
          >
            Refine
          </button>
        )}
      </div>
    </div>
  );
}
